"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { math } from "polished";
import { v4 as uuid } from "uuid";
import renderIcon from "../../icons/render-icon.js";
import makeStyles from "../../theming/make-styles.js";
import isElement from "../utils/is-element.js";
import InputBox, { paddings } from "./input-box.js";
import { InputGroupContext } from "./input-group.js";
const useStyles = makeStyles()((theme, { color, resize, size, variant }) => {
    const palette = theme.palette(color).input;
    return {
        input: {
            "width": "100%",
            "minWidth": 0,
            "minHeight": math(`${theme.typography.body.lineHeight} * ${theme.typography.body.fontSize}`),
            "padding": 0,
            "fontFamily": "inherit",
            "fontSize": "inherit",
            "lineHeight": theme.typography.body.lineHeight,
            "color": palette.color,
            "backgroundColor": palette.backgroundColor,
            "border": 0,
            "cursor": "inherit",
            resize,
            "userSelect": "inherit",
            "&:focus": {
                outline: 0,
            },
            "&[size]": {
                width: "auto",
            },
        },
        append: {
            display: "inline-flex",
            marginLeft: theme.spacing(paddings[size].x),
            color: variant ? palette[variant].append.color : undefined,
            lineHeight: 1,
        },
        inputGroupBox: {
            alignSelf: "stretch",
            minWidth: 0,
            borderWidth: 0,
            boxShadow: "none",
        },
        leftIcon: {
            flexShrink: 0,
            width: size === "small" ? theme.size(18) : theme.size(20),
            height: size === "small" ? theme.size(18) : theme.size(20),
            marginRight: theme.spacing(paddings[size].x),
        },
        rightIcon: {
            width: size === "small" ? theme.size(18) : theme.size(20),
            height: size === "small" ? theme.size(18) : theme.size(20),
            flexShrink: 0,
            marginLeft: theme.spacing(paddings[size].x),
        },
        prepend: {
            display: "inline-flex",
            marginRight: theme.spacing(paddings[size].x),
            color: variant ? palette[variant].prepend.color : undefined,
            lineHeight: 1,
        },
    };
});
export const Input = React.forwardRef(function Input(_a, ref) {
    var { append, block, className, color = "gray", disabled, leftIcon, length, multiline, prepend, rightIcon, shadow, size = "medium", valid, variant, onBlur, onChange, onFocus } = _a, props = __rest(_a, ["append", "block", "className", "color", "disabled", "leftIcon", "length", "multiline", "prepend", "rightIcon", "shadow", "size", "valid", "variant", "onBlur", "onChange", "onFocus"]);
    const [focused, setFocused] = useState(false);
    const { inputGroup } = useContext(InputGroupContext);
    const resize = multiline ? props.resize || "both" : "none";
    const { classes, cx } = useStyles({ color, resize, size, variant });
    const id = useMemo(() => uuid(), []);
    const inputRef = useRef(null);
    const handleBlur = useCallback((e) => {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        setFocused(false);
    }, [onBlur]);
    const handleChange = useCallback((e) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value, e);
    }, [onChange]);
    const handleFocus = useCallback((e) => {
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
        setFocused(true);
    }, [onFocus]);
    delete props["resize"];
    const inputProps = Object.assign({ className: classes.input, disabled,
        id, maxLength: length, size: length, onBlur: handleBlur, onChange: handleChange, onFocus: handleFocus }, props);
    return (_jsxs(InputBox, { ref: ref, block: block, className: cx(inputGroup && classes.inputGroupBox, className), color: color, "data-component": "input", disabled: disabled, focused: focused, htmlFor: id, shadow: shadow, size: size, valid: valid, variant: variant, onClick: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, children: [isElement(prepend)
                ? React.cloneElement(prepend, {
                    className: cx(classes.prepend, prepend.props.className),
                })
                : prepend && _jsx("span", { className: classes.prepend, children: prepend }), renderIcon(leftIcon, classes.leftIcon), multiline ? _jsx("textarea", Object.assign({}, inputProps)) : _jsx("input", Object.assign({ ref: inputRef }, inputProps)), renderIcon(rightIcon, classes.rightIcon), isElement(append)
                ? React.cloneElement(append, {
                    className: cx(classes.append, append.props.className),
                })
                : append && _jsx("span", { className: classes.append, children: append })] }));
});
export default Input;
//# sourceMappingURL=input.js.map